/* Shared state: the world (loaded once), the theme, toasts, and a small async hook. */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { api } from './api.ts'
import { hold } from './pending.ts'
import { withTransition } from './router.tsx'
import type { WorldInfo } from './types.ts'

export type Async<T> = { data: T | null; error: Error | null; loading: boolean; reload: () => void }

export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[]): Async<T> {
  const [state, setState] = useState<{ data: T | null; error: Error | null; loading: boolean }>({ data: null, error: null, loading: true })
  const [tick, setTick] = useState(0)
  // Whether this hook has ever had something to show; only a first read is counted as loading (lib/pending.ts).
  const shown = useRef(false)
  const held = useRef<(() => void) | null>(null)
  // A layout effect, so a first read is counted before the first paint and the page's body keeps its height from the start.
  useLayoutEffect(() => {
    const ctl = new AbortController()
    held.current = shown.current ? null : hold()
    setState((s) => ({ ...s, loading: true, error: null }))
    fn(ctl.signal).then(
      (data) => {
        if (ctl.signal.aborted) return
        shown.current = true
        setState({ data, error: null, loading: false })
      },
      (error: Error) => !ctl.signal.aborted && error.name !== 'AbortError' && setState({ data: null, error, loading: false }),
    )
    return () => {
      ctl.abort()
      held.current?.()
      held.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])
  // The read is over in the commit that draws what it brought, so the body gives its height back with the data in it, not before.
  useLayoutEffect(() => {
    if (!state.loading) {
      held.current?.()
      held.current = null
    }
  }, [state.loading])
  return { ...state, reload: () => setTick((t) => t + 1) }
}

const WorldCtx = createContext<Async<WorldInfo>>({ data: null, error: null, loading: true, reload: () => undefined })

export function WorldProvider({ children }: { children: ReactNode }) {
  const world = useAsync((s) => api.world(s), [])
  return <WorldCtx.Provider value={world}>{children}</WorldCtx.Provider>
}
export const useWorld = () => useContext(WorldCtx)

/** Light is the default for every reader; dark is a toggle, remembered once chosen (public/theme.js applies it before the first paint). */
export type Theme = 'light' | 'dark'
const THEME_KEY = 'damaros.theme'

function readTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

type ThemeApi = { theme: Theme; resolved: Theme; set: (t: Theme) => void; toggle: () => void }

function useThemeState(): ThemeApi {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const set = useCallback((t: Theme) => {
    setTheme(t)
    try {
      if (t === 'dark') localStorage.setItem(THEME_KEY, t)
      else localStorage.removeItem(THEME_KEY)
    } catch {
      // the choice still holds for this visit
    }
    const apply = () => {
      document.documentElement.dataset.theme = t
    }
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) withTransition(apply)
    else apply()
  }, [])
  return { theme, resolved: theme, set, toggle: () => set(theme === 'dark' ? 'light' : 'dark') }
}

const ThemeCtx = createContext<ThemeApi>({ theme: 'light', resolved: 'light', set: () => undefined, toggle: () => undefined })
export function ThemeProvider({ children }: { children: ReactNode }) {
  return <ThemeCtx.Provider value={useThemeState()}>{children}</ThemeCtx.Provider>
}
export const useTheme = () => useContext(ThemeCtx)

type Toast = { id: number; text: string; tone?: 'info' | 'fail' }
const ToastCtx = createContext<(text: string, tone?: Toast['tone']) => void>(() => undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const next = useRef(1)
  const push = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    const id = next.current++
    setToasts((t) => [...t.slice(-3), { id, text, tone }])
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6500)
  }, [])
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="toast" style={t.tone === 'fail' ? { borderColor: 'color-mix(in oklch, var(--danger) 45%, var(--border))' } : undefined}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="8" cy="8" r="3.5" fill={t.tone === 'fail' ? 'var(--danger)' : 'var(--accent)'} />
            </svg>
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}
export const useToast = () => useContext(ToastCtx)

/** Width of an element, for charts that draw to their container. */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null)
  const [w, setW] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)))
    ro.observe(el)
    setW(Math.round(el.getBoundingClientRect().width))
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}
